import { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

function ConfirmButton({ intentType, returnUrl }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const confirm = async () => {
    if (!stripe || !elements) return;
    setPending(true);
    setError("");
    const result =
      intentType === "setup"
        ? await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: returnUrl },
          })
        : await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: returnUrl },
          });
    if (result.error) {
      setError(result.error.message || "Payment could not be confirmed");
      setPending(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={confirm}
        disabled={!stripe || pending}
        className="h-12 rounded-md bg-black text-white font-medium disabled:opacity-60"
      >
        {pending
          ? "Confirming..."
          : intentType === "setup"
            ? "Start free trial"
            : "Pay now"}
      </button>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
}

export default function StripePaymentForm({ session }) {
  const stripePromise = useMemo(() => {
    if (!session?.publishableKey) return null;
    return loadStripe(session.publishableKey, {
      stripeAccount: session.stripeAccountId,
    });
  }, [session?.publishableKey, session?.stripeAccountId]);
  const returnUrl = useMemo(() => {
    const url = new URL(window.location.href);
    if (session?.purchaseId) {
      url.searchParams.set("stripe_purchase_id", session.purchaseId);
    }
    return url.toString();
  }, [session?.purchaseId]);

  if (!session?.clientSecret || !stripePromise) return null;

  const amount = Number(session.subscriptionAmount || session.amount || 0) / 100;
  const formatted = new Intl.NumberFormat(
    session.currency === "EUR" ? "de-DE" : "en-US",
    {
      style: "currency",
      currency: session.currency || "USD",
    }
  ).format(amount);
  const isTrial = session.intentType === "setup";

  return (
    <div className="mt-3 flex flex-col gap-3">
      <p className="text-sm font-medium">
        {isTrial
          ? `Secure card setup · ${session.trialDays} day free trial, then ${formatted}`
          : `Secure payment · ${formatted}`}
      </p>
      <Elements
        stripe={stripePromise}
        options={{ clientSecret: session.clientSecret, appearance: { theme: "stripe" } }}
      >
        <PaymentElement />
        <ConfirmButton
          intentType={session.intentType}
          returnUrl={returnUrl}
        />
      </Elements>
    </div>
  );
}
